import { memo } from "react";

import { Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import { mergeSx } from "../../../common/helpers/mergeSx";

const VIBE_STYLES = {
  brand: {
    track: (theme: Theme) => theme.palette.surface.brand.quiet,
    fill: (theme: Theme) => theme.palette.text.brand.quiet,
    trackShadow: "0 1px 8px 0 rgba(85, 85, 231, 0.10) inset",
  },
  positive: {
    track: (theme: Theme) => theme.palette.surface.neutral.quiet,
    fill: (theme: Theme) => theme.palette.surface.positive.intense,
    trackShadow: "none",
  },
} as const;

interface ProgressProps {
  height?: number;
  value: number;
  vibe?: keyof typeof VIBE_STYLES;
  startRounded?: boolean;
  endRounded?: boolean;
  innerEndRounded?: boolean;
  sx?: SxProps<Theme>;
  transitionSpeed?: number;
  "data-test"?: string;
}

export const DashboardProgress = memo(
  ({
    height = 7,
    value,
    vibe = "brand",
    startRounded,
    endRounded,
    innerEndRounded,
    sx,
    transitionSpeed = 2,
    "data-test": dataTest,
  }: ProgressProps) => {
    const innerBaseStyle = {
      height,
      borderTopLeftRadius: startRounded ? 4 : 0,
      borderBottomLeftRadius: startRounded ? 4 : 0,
      borderTopRightRadius: innerEndRounded ? 4 : 0,
      borderBottomRightRadius: innerEndRounded ? 4 : 0,
    };

    const outerBaseStyle = {
      borderTopLeftRadius: startRounded ? 4 : 0,
      borderBottomLeftRadius: startRounded ? 4 : 0,
      borderTopRightRadius: endRounded ? 4 : 0,
      borderBottomRightRadius: endRounded ? 4 : 0,
      height,
    };

    return (
      <Box
        data-test={dataTest}
        sx={mergeSx(
          (theme) => ({
            width: "100%",
            backgroundColor: VIBE_STYLES[vibe].track(theme),
            boxShadow: VIBE_STYLES[vibe].trackShadow,
          }),
          outerBaseStyle,
          sx
        )}
      >
        <Box
          sx={mergeSx(
            (theme) => ({
              backgroundColor: VIBE_STYLES[vibe].fill(theme),
              width: `${value}%`,
              willChange: "width",
              transition: `width ${transitionSpeed}s linear`,
            }),
            innerBaseStyle
          )}
        />
      </Box>
    );
  }
);

DashboardProgress.displayName = "DashboardProgress";
